import { useCallback, useEffect, useState } from "react";
import { getConnection, saveConnection, testConnection, type QaConnection, type QaConnectionSource, type QaGithubAppStatus } from "../../api/qaOrchestrator";
import { relativeTime } from "./memberModel";
import { useAction, type SetError } from "./qaShared";

const SOURCE: Record<QaConnectionSource, string> = { db: "saved here", env: "from server env", none: "not set" };
const tokenLine = (c: QaConnection) => (c.tokenSet ? `set (${SOURCE[c.source.token]})` : "not set");

/** The workspace's GitHub App, which the orchestrator reads private repos with; set in the server env only. */
function githubLine(g: QaGithubAppStatus): { ok: boolean; text: string } {
  if (g.app === "ok") return { ok: true, text: "working" };
  if (g.app === "not_configured") return { ok: false, text: "not configured: set GITHUB_APP_ID, GITHUB_APP_INSTALLATION_ID, GITHUB_APP_PRIVATE_KEY_PATH on the server" };
  return { ok: false, text: `failing: ${g.error ?? "unknown error"}` };
}

/**
 * Where the daemon reaches the orchestrator (GET/PUT /admin/qa-orchestrator/connection).
 * A value saved here wins over the server env; an empty field uses the env. Reachable
 * while nothing is configured yet, since this is where it gets configured.
 */
export default function QaConnectionSection({ setError, onSaved }: { setError: SetError; onSaved: () => Promise<void> }) {
  const [conn, setConn] = useState<QaConnection | null>(null);
  const [form, setForm] = useState({ url: "", publicUrl: "", token: "" });
  const [test, setTest] = useState<{ ok: boolean; status: number; error?: string } | null>(null);

  const apply = useCallback((c: QaConnection) => {
    setConn(c);
    // The fields show what is saved here; empty means the server env applies. The token never comes back.
    setForm({ url: c.source.url === "db" ? c.url ?? "" : "", publicUrl: c.source.publicUrl === "db" ? c.publicUrl ?? "" : "", token: "" });
  }, []);
  const load = useCallback(async () => apply(await getConnection()), [apply]);
  const reload = useCallback(async () => { await onSaved(); }, [onSaved]);
  const { busy, run } = useAction(reload, setError);
  useEffect(() => { load().catch((e) => setError((e as Error).message)); }, [load, setError]);

  if (!conn) return <section className="qo-section"><div className="members-empty">Loading…</div></section>;

  const savedUrl = conn.source.url === "db" ? conn.url ?? "" : "";
  const savedPublic = conn.source.publicUrl === "db" ? conn.publicUrl ?? "" : "";
  const patch = {
    ...(form.url.trim() !== savedUrl ? { url: form.url.trim() || null } : {}),
    ...(form.publicUrl.trim() !== savedPublic ? { publicUrl: form.publicUrl.trim() || null } : {}),
    ...(form.token ? { token: form.token } : {}),
  };
  const changed = Object.keys(patch).length > 0;
  const save = (p: Parameters<typeof saveConnection>[0]) => run(async () => {
    apply(await saveConnection(p));
    setTest(null);
  });
  const tryIt = () => run(async () => {
    setTest(await testConnection({ ...(form.url.trim() ? { url: form.url.trim() } : {}), ...(form.token ? { token: form.token } : {}) }));
  });
  const envHint = (source: QaConnectionSource, value: string | null, fallback: string) =>
    source === "env" ? `Empty: from server env (${value})` : source === "db" ? "Saved here. Empty it and save to use the server env." : `Not set. ${fallback}`;

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Connection</h3>
          <p>
            Where this workspace reaches the qa-agent orchestrator, and the admin token it uses. A value saved here wins over the server env
            (<code>QA_ORCHESTRATOR_URL</code>, <code>QA_ORCHESTRATOR_PUBLIC_URL</code>, <code>QA_ORCHESTRATOR_TOKEN</code>) and applies at once, without a restart.
          </p>
        </div>
      </div>
      <div className="qo-card qo-form">
        {!conn.configured && <p className="qo-hint qo-hint-tight">Not connected yet: save the orchestrator URL and its admin token below.</p>}
        <div className="qo-grid">
          <label>
            Orchestrator URL
            <input aria-label="Orchestrator URL" type="url" placeholder={conn.source.url === "env" ? conn.url ?? "" : "https://qa-orchestrator.example.org"} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            <small>{envHint(conn.source.url, conn.url, "The daemon's address for the orchestrator.")}</small>
          </label>
          <label>
            Public URL
            <input aria-label="Public URL" type="url" placeholder={conn.source.publicUrl === "env" ? conn.publicUrl ?? "" : conn.url ?? "https://qa.example.org"} value={form.publicUrl} onChange={(e) => setForm({ ...form, publicUrl: e.target.value })} />
            <small>{envHint(conn.source.publicUrl, conn.publicUrl, "The orchestrator URL is used, for CI and links.")}</small>
          </label>
          <label>
            API token
            <input aria-label="API token" type="password" autoComplete="new-password" placeholder="Enter a new token to replace it" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} />
            <small>Token: {tokenLine(conn)}</small>
          </label>
        </div>
        <div className="qo-actions">
          <button className="btn btn-accent" disabled={busy || !changed} onClick={() => void save(patch)}>Save</button>
          <button className="btn btn-ghost" disabled={busy || (!conn.configured && !(form.url.trim() && form.token))} onClick={() => void tryIt()}>Test connection</button>
          <button className="btn btn-ghost" disabled={busy || conn.source.token !== "db"} onClick={() => void save({ token: null })}>Clear saved token</button>
          {test && (
            <span role="status">
              <span className={`qo-dot ${test.ok ? "qo-dot-ok" : "qo-dot-bad"}`} />
              {test.ok ? "Connected: the token works." : `Failed (${test.status}): ${test.error ?? "unknown error"}`}
            </span>
          )}
        </div>
        {conn.github && (
          <p className="qo-hint" data-testid="qa-github-access">
            <span className={`qo-dot ${githubLine(conn.github).ok ? "qo-dot-ok" : "qo-dot-bad"}`} />
            GitHub access (this workspace's GitHub App): {githubLine(conn.github).text}
          </p>
        )}
        <p className="qo-hint">
          Test connection tries what is typed above before it is saved (an empty field: the value in effect).
          {conn.updatedAt ? ` Last saved ${relativeTime(new Date(conn.updatedAt).toISOString())}${conn.updatedBy ? ` by ${conn.updatedBy}` : ""}.` : ""}
        </p>
      </div>
    </section>
  );
}
